import { getAllEvents, getEventsForReligion, getEventsForNature, getCountries, getMinEventsThreshold } from '@/lib/data-source';
import { CALENDAR_YEAR } from '@/lib/config';
import { ui, uiFb } from '@/lib/i18n-helpers';
import { getMonthFromDate } from '@/lib/date-format';
import { parseLocalDate } from '@/lib/date-calc';
import { canonicalEventKey, eventCountries, isVariableDateKey, VARIABLE_DATE_WINDOW_DAYS } from '@/lib/event-canonical';
import type { CalendarEvent, CountryCode, SubLensId, ReligionSubLensId, EventType, PillNavLink } from '@/lib/types';

const YEAR = CALENDAR_YEAR;

const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const EVENT_TYPE_SLUGS: Record<string, EventType> = {
  'public-holidays': 'public_holiday',
  'national-days': 'national_day',
  'observances': 'observance',
  'cultural-festivals': 'cultural_festival',
  'local-events': 'local_event',
};

/** Phrase key for each event type slug (slug uses hyphens, key uses underscores) */
const TYPE_SLUG_TO_PHRASE_KEY: Record<string, string> = {
  'public-holidays': 'hub.type.public_holidays',
  'national-days': 'hub.type.national_days',
  'observances': 'hub.type.observances',
  'cultural-festivals': 'hub.type.cultural_festivals',
  'local-events': 'hub.type.local_events',
};

const RELIGION_SLUGS: Record<string, ReligionSubLensId> = {
  'islamic': 'islamic',
  'hindu': 'hindu',
  'lunar': 'lunar',
  'christian': 'christian',
  'buddhist': 'buddhist',
};

// --- i18n display helpers ---

const MONTH_DISPLAY_EN: Record<string, string> = {
  january: 'January', february: 'February', march: 'March',
  april: 'April', may: 'May', june: 'June',
  july: 'July', august: 'August', september: 'September',
  october: 'October', november: 'November', december: 'December',
};

const RELIGION_DISPLAY_EN: Record<string, string> = {
  islamic: 'Islamic', hindu: 'Hindu', lunar: 'Lunar',
  christian: 'Christian', buddhist: 'Buddhist',
};

const TYPE_DISPLAY_EN: Record<string, string> = {
  'public-holidays': 'Public Holidays', 'national-days': 'National Days',
  'observances': 'Observances', 'cultural-festivals': 'Cultural Festivals',
  'local-events': 'Local Events',
};

/** Returns the localized display name for a month (1-indexed). */
export function getMonthDisplay(month: number, lang: string = 'en'): string {
  const name = MONTH_NAMES[month - 1];
  return uiFb(`ui.month.${name}`, MONTH_DISPLAY_EN[name] ?? name, lang);
}

/** Returns the localized display name for an event type slug (e.g. 'public-holidays'). */
export function getEventTypeDisplay(slug: string, lang: string = 'en'): string {
  const phraseKey = TYPE_SLUG_TO_PHRASE_KEY[slug];
  if (!phraseKey) return TYPE_DISPLAY_EN[slug] ?? slug;
  return uiFb(phraseKey, TYPE_DISPLAY_EN[slug] ?? slug, lang);
}

/** Returns the localized display name for a religion key (e.g. 'islamic'). */
export function getReligionDisplay(key: string, lang: string = 'en'): string {
  return uiFb(`hub.religion.${key}`, RELIGION_DISPLAY_EN[key] ?? key, lang);
}

// --- Slug types ---

export type HubKind = 'month' | 'type' | 'religion' | 'nature';

export interface MonthHub {
  kind: 'month';
  month: number; // 1-12
  year: number;
  slug: string;
  displayName: string;
}

export interface TypeHub {
  kind: 'type';
  eventType: EventType;
  slug: string;
  displayName: string;
}

export interface ReligionHub {
  kind: 'religion';
  subLens: ReligionSubLensId;
  slug: string;
  displayName: string;
}

export interface NatureHub {
  kind: 'nature';
  slug: string;
  displayName: string;
}

export type HubSlug = MonthHub | TypeHub | ReligionHub | NatureHub;

// --- Slug parser ---

const HUB_SUFFIX = '-worldwide-calendar';

export function parseHubSlug(slug: string, lang: string = 'en'): HubSlug | null {
  // Try month: "march-2026-worldwide-calendar"
  for (let i = 0; i < MONTH_NAMES.length; i++) {
    if (slug === `${MONTH_NAMES[i]}-${YEAR}${HUB_SUFFIX}`) {
      return {
        kind: 'month',
        month: i + 1,
        year: YEAR,
        slug,
        displayName: getMonthDisplay(i + 1, lang),
      };
    }
  }

  // Try religion: "islamic-calendar-2026-worldwide-calendar"
  for (const [key, subLens] of Object.entries(RELIGION_SLUGS)) {
    if (slug === `${key}-calendar-${YEAR}${HUB_SUFFIX}`) {
      return {
        kind: 'religion',
        subLens,
        slug,
        displayName: getReligionDisplay(key, lang),
      };
    }
  }

  // Try nature: "nature-calendar-2026-worldwide-calendar"
  if (slug === `nature-calendar-${YEAR}${HUB_SUFFIX}`) {
    return {
      kind: 'nature',
      slug,
      displayName: uiFb('hub.nature', 'Nature & Seasons', lang),
    };
  }

  // Try event type: "public-holidays-worldwide-calendar"
  for (const [typeSlug, eventType] of Object.entries(EVENT_TYPE_SLUGS)) {
    if (slug === `${typeSlug}${HUB_SUFFIX}`) {
      return {
        kind: 'type',
        eventType,
        slug,
        displayName: getEventTypeDisplay(typeSlug, lang),
      };
    }
  }

  return null;
}

// --- Static params ---

export function getAllHubSlugs(): string[] {
  const slugs: string[] = [];

  // Month slugs
  for (const name of MONTH_NAMES) {
    slugs.push(`${name}-${YEAR}${HUB_SUFFIX}`);
  }

  // Event type slugs
  const hubThreshold = getMinEventsThreshold('hub');
  for (const typeSlug of Object.keys(EVENT_TYPE_SLUGS)) {
    if (hubThreshold > 0 && getEventsForType(EVENT_TYPE_SLUGS[typeSlug]).length < hubThreshold) continue;
    slugs.push(`${typeSlug}${HUB_SUFFIX}`);
  }

  // Religion slugs
  for (const key of Object.keys(RELIGION_SLUGS)) {
    slugs.push(`${key}-calendar-${YEAR}${HUB_SUFFIX}`);
  }

  // Nature slug
  slugs.push(`nature-calendar-${YEAR}${HUB_SUFFIX}`);

  return slugs;
}

// --- Data aggregation ---

export function getEventsForMonth(month: number): CalendarEvent[] {
  const monthStr = String(month).padStart(2, '0');
  return getAllEvents()
    .filter((e) => e.visibility === 'global')
    .filter((e) => e.dateStart.slice(5, 7) === monthStr);
}

export function getEventsForType(eventType: EventType): CalendarEvent[] {
  return getAllEvents()
    .filter((e) => e.visibility === 'global')
    .filter((e) => e.type === eventType);
}

export function getEventsForReligionHub(subLens: ReligionSubLensId): CalendarEvent[] {
  return getEventsForReligion(subLens).filter(e => e.visibility === 'global');
}

/** Picks the row whose title best represents a merged cluster: the most common
 *  title wins, and a placeholder "(estimated)" title only wins if nothing else
 *  is available. */
function pickRepresentative(cluster: CalendarEvent[]): CalendarEvent {
  const tally = new Map<string, number>();
  for (const e of cluster) tally.set(e.title, (tally.get(e.title) ?? 0) + 1);

  let best = cluster[0];
  let bestScore = -Infinity;
  for (const e of cluster) {
    const score = (tally.get(e.title) ?? 0) * 10 - (/\(estimated\)/i.test(e.title) ? 5 : 0);
    if (score > bestScore) {
      best = e;
      bestScore = score;
    }
  }
  return best;
}

/** Collapses a cluster into one event spanning every member's dates and countries. */
function mergeCluster(cluster: CalendarEvent[]): CalendarEvent {
  const rep = pickRepresentative(cluster);
  const countries = Array.from(new Set(cluster.map((e) => e.country)));
  if (cluster.length === 1) return { ...rep, countries };

  let dateStart = cluster[0].dateStart;
  let dateEnd = cluster[0].dateEnd;
  for (const e of cluster) {
    if (e.dateStart < dateStart) dateStart = e.dateStart;
    if (e.dateEnd > dateEnd) dateEnd = e.dateEnd;
  }

  const startDate = parseLocalDate(dateStart);
  return {
    ...rep,
    countries,
    dateStart,
    dateEnd,
    // Keep the pre-parsed fields consistent with the widened range.
    year: startDate.getFullYear(),
    startMs: startDate.getTime(),
    endMs: parseLocalDate(dateEnd).getTime(),
    startDay: startDate.getDate(),
  };
}

/**
 * Deduplicate events for worldwide hubs, merging country codes into a
 * `countries` array.
 *
 * Grouping is by canonical holiday key (see `event-canonical.ts`), which
 * collapses the spelling and `event_group` variants one holiday arrives under.
 * Holidays whose Gregorian date differs by country — Mawlid, Eid, Vesak — are
 * additionally clustered across a short date window, because an exact-date key
 * can never merge a moon-sighting spread (Mawlid 2026 runs Aug 24-27). Fixed-date
 * holidays keep exact-date grouping so same-named but unrelated national
 * holidays stay apart.
 */
export function deduplicateWorldwideEvents(events: CalendarEvent[]): CalendarEvent[] {
  // Bucket by canonical key first, then cluster each bucket by date.
  const buckets = new Map<string, CalendarEvent[]>();
  const order: string[] = [];
  for (const e of events) {
    const key = canonicalEventKey(e);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      order.push(key);
    }
    bucket.push(e);
  }

  const merged: CalendarEvent[] = [];
  for (const key of order) {
    const bucket = buckets.get(key)!;
    const windowDays = isVariableDateKey(key) ? VARIABLE_DATE_WINDOW_DAYS : 0;
    const sorted = [...bucket].sort(
      (a, b) => a.dateStart.localeCompare(b.dateStart) || a.id.localeCompare(b.id)
    );

    let cluster: CalendarEvent[] = [];
    let clusterStart = '';
    for (const e of sorted) {
      const withinWindow =
        cluster.length > 0 &&
        (windowDays === 0
          ? e.dateStart === clusterStart
          : Math.round(
              (parseLocalDate(e.dateStart).getTime() - parseLocalDate(clusterStart).getTime()) / 86_400_000
            ) <= windowDays);

      if (withinWindow) {
        cluster.push(e);
      } else {
        if (cluster.length) merged.push(mergeCluster(cluster));
        cluster = [e];
        clusterStart = e.dateStart;
      }
    }
    if (cluster.length) merged.push(mergeCluster(cluster));
  }

  return merged.sort((a, b) => a.dateStart.localeCompare(b.dateStart) || a.id.localeCompare(b.id));
}

export function getEventsForNatureHub(): CalendarEvent[] {
  return getEventsForNature().filter(e => e.visibility === 'global');
}

// --- Premium gate ---

export const FREE_COUNTRY_COUNT = 3;
export const FREE_MONTH_COUNT = 3;

/** Returns the current month + next 2 months as a Set of month numbers (1-12). */
export function getFreeMonths(): Set<number> {
  const now = new Date();
  const current = now.getMonth() + 1; // 1-indexed
  const free = new Set<number>();
  for (let i = 0; i < FREE_MONTH_COUNT; i++) {
    free.add(((current - 1 + i) % 12) + 1);
  }
  return free;
}

/** Returns the top N country codes by event count (most events first). */
export function getFreeCountries(events: CalendarEvent[], count = FREE_COUNTRY_COUNT): Set<string> {
  const tally = new Map<string, number>();
  for (const e of events) {
    // Merged hub rows cover many countries; tally them all, or a country whose
    // holidays all merged under another country's row would never rank.
    for (const code of eventCountries(e)) {
      tally.set(code, (tally.get(code) || 0) + 1);
    }
  }
  const sorted = [...tally.entries()].sort((a, b) => b[1] - a[1]);
  return new Set(sorted.slice(0, count).map(([code]) => code));
}

// --- Group helpers ---

export function groupEventsByCountry(events: CalendarEvent[]): Record<CountryCode, CalendarEvent[]> {
  const map: Record<string, CalendarEvent[]> = {};
  const countries = getCountries();
  for (const event of events) {
    if (!map[event.country]) map[event.country] = [];
    map[event.country].push(event);
  }
  // Sort by country order
  const ordered: Record<string, CalendarEvent[]> = {};
  for (const c of countries) {
    if (map[c.code]) ordered[c.code] = map[c.code];
  }
  return ordered as Record<CountryCode, CalendarEvent[]>;
}

export function groupEventsByWeek(events: CalendarEvent[]): { weekStart: string; events: CalendarEvent[] }[] {
  const sorted = [...events].sort((a, b) => a.dateStart.localeCompare(b.dateStart));
  const weeks: { weekStart: string; events: CalendarEvent[] }[] = [];

  for (const event of sorted) {
    const d = new Date(event.dateStart + 'T00:00:00');
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((day + 6) % 7));
    const weekKey = monday.toISOString().slice(0, 10);

    const existing = weeks.find((w) => w.weekStart === weekKey);
    if (existing) {
      existing.events.push(event);
    } else {
      weeks.push({ weekStart: weekKey, events: [event] });
    }
  }

  return weeks;
}

// --- Sibling links ---

/** @deprecated Use PillNavLink from @/lib/types instead */
export type HubSiblingLink = PillNavLink;

export function getHubSiblings(currentSlug: string, lang: string = 'en'): PillNavLink[] {
  const hub = parseHubSlug(currentSlug, lang);
  if (!hub) return [];

  switch (hub.kind) {
    case 'month':
      return MONTH_NAMES.map((name, i) => ({
        label: getMonthDisplay(i + 1, lang),
        href: `/${name}-${YEAR}-worldwide-calendar`,
        active: hub.month === i + 1,
      }));
    case 'type': {
      const typeThreshold = getMinEventsThreshold('hub');
      return Object.entries(EVENT_TYPE_SLUGS)
        .filter(([, type]) => getEventsForType(type).length >= typeThreshold)
        .map(([slug, type]) => ({
          label: getEventTypeDisplay(slug, lang),
          href: `/${slug}-worldwide-calendar`,
          active: hub.eventType === type,
        }));
    }
    case 'religion':
      return Object.entries(RELIGION_SLUGS).map(([key, subLens]) => ({
        label: `${getReligionDisplay(key, lang)} ${uiFb('hub.calendar', 'Calendar', lang)}`,
        href: `/${key}-calendar-${YEAR}-worldwide-calendar`,
        active: hub.subLens === subLens,
      }));
    case 'nature':
      // Only one nature hub, no siblings
      return [];
  }
}

// --- Reverse map: event type → hub slug ---

export const EVENT_TYPE_TO_HUB: Record<string, string> = Object.fromEntries(
  Object.entries(EVENT_TYPE_SLUGS).map(([slug, type]) => [type, `${slug}-worldwide-calendar`])
);

// --- Exports for footer links ---

// --- Unique event count (collapses same event_group into one) ---

/** Count unique events, collapsing shared event_groups into one. */
export function getUniqueEventCount(events?: CalendarEvent[]): number {
  const all = events ?? getAllEvents();
  const seen = new Set<string>();
  let count = 0;
  for (const e of all) {
    if (e.eventGroup) {
      if (!seen.has(e.eventGroup)) {
        seen.add(e.eventGroup);
        count++;
      }
    } else {
      count++;
    }
  }
  return count;
}

/** Returns the hub slug for a religion sub-lens (e.g. 'islamic' → 'islamic-calendar-2026-worldwide-calendar'). */
export function getReligionHubSlug(subLens: string): string | null {
  const key = Object.keys(RELIGION_SLUGS).find(k => RELIGION_SLUGS[k] === subLens);
  if (!key) return null;
  return `${key}-calendar-${YEAR}-worldwide-calendar`;
}

/** Deduplicate events by title, collecting which countries observe each. */
export function deduplicateEventsByTitle(events: CalendarEvent[]): Array<{ event: CalendarEvent; countryCodes: Set<string> }> {
  const eventMap = new Map<string, { event: CalendarEvent; countryCodes: Set<string> }>();
  for (const event of events) {
    const key = event.title;
    if (eventMap.has(key)) {
      eventMap.get(key)!.countryCodes.add(event.country);
    } else {
      eventMap.set(key, { event, countryCodes: new Set([event.country]) });
    }
  }
  return Array.from(eventMap.values());
}

/** Split items into free/locked buckets based on the current free months window. */
export function splitByFreeMonths<T>(
  items: T[],
  getDateStr: (item: T) => string,
): { free: T[]; locked: T[] } {
  const freeMonths = getFreeMonths();
  return {
    free: items.filter(item => freeMonths.has(getMonthFromDate(getDateStr(item)))),
    locked: items.filter(item => !freeMonths.has(getMonthFromDate(getDateStr(item)))),
  };
}

/** Count events per subLens value (defaults to 'other' when absent). */
export function countBySubLens(events: CalendarEvent[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const e of events) {
    const sub = e.subLens || 'other';
    counts[sub] = (counts[sub] || 0) + 1;
  }
  return counts;
}

export { MONTH_NAMES, EVENT_TYPE_SLUGS, RELIGION_SLUGS, YEAR as HUB_YEAR };
